'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Sparkles, Wand2 } from 'lucide-react';

import { useI18n } from '@/contexts/I18nContext';
import { useInViewport } from '@/hooks/useInViewport';
import type { SuggestedVideoDTO } from '@/lib/studio/suggestedVideos';

// Витрина курируется вручную и меняется редко — переживает переходы
// Студия ↔ Библиотека без повторного запроса.
let memo: SuggestedVideoDTO[] | null = null;

const ASPECT_CLASS: Record<string, string> = {
  '9:16': 'aspect-[9/16]',
  '16:9': 'aspect-video',
  '4:3': 'aspect-[4/3]',
  '1:1': 'aspect-square',
};

/**
 * Готовые примеры «ролик + промпт, которым он сделан».
 *
 * Пустое поле ввода — худшее, что можно показать человеку, который первый раз
 * пришёл генерировать рекламу: он не знает, ни что просить, ни насколько
 * подробно. Здесь он видит результат и забирает промпт одним нажатием.
 *
 * Каталог наш собственный (`/api/studio/suggested-videos`), в отличие от блока
 * примеров WaveSpeed ниже: там витрина провайдера с чужими промптами.
 */
export function SuggestedVideos({ onUsePrompt }: { onUsePrompt?: (prompt: string) => void }) {
  const { t } = useI18n();
  const [items, setItems] = useState<SuggestedVideoDTO[]>(() => memo ?? []);
  const [loading, setLoading] = useState(() => memo === null);

  useEffect(() => {
    if (memo !== null) return;
    let cancelled = false;
    fetch('/api/studio/suggested-videos')
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        const videos = (json?.videos ?? []) as SuggestedVideoDTO[];
        memo = videos;
        setItems(videos);
      })
      .catch(() => {
        // Витрина необязательна: молча прячем блок, а не пугаем ошибкой на
        // главном экране генерации.
        if (!cancelled) memo = [];
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={`aspect-[9/16] animate-pulse rounded-2xl border border-white/10 bg-white/5 ${
              i >= 2 ? 'hidden sm:block' : ''
            } ${i >= 3 ? 'sm:hidden lg:block' : ''}`}
          />
        ))}
      </div>
    );
  }

  // Нечего показать — блока просто нет. Заголовок над пустотой выглядел бы
  // как поломка.
  if (items.length === 0) return null;

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-300" />
        <div>
          <h2 className="text-lg font-bold text-white sm:text-xl">
            {t('studio.suggested.heading')}
          </h2>
          <p className="mt-0.5 text-sm text-white/55">{t('studio.suggested.hint')}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((item) => (
          <SuggestedCard key={item.id} item={item} onUsePrompt={onUsePrompt} />
        ))}
      </div>
    </div>
  );
}

function SuggestedCard({
  item,
  onUsePrompt,
}: {
  item: SuggestedVideoDTO;
  onUsePrompt?: (prompt: string) => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Ролик не тянем, пока карточка не подошла к экрану: витрина из двух десятков
  // видео иначе дёрнула бы метаданные всех сразу.
  const [cardRef, inView] = useInViewport<HTMLDivElement>();

  const play = () => videoRef.current?.play().catch(() => {});
  const stop = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  const copy = () => {
    navigator.clipboard?.writeText(item.prompt).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  const hasThumb = Boolean(item.thumbnailUrl);

  return (
    <div
      ref={cardRef}
      onMouseEnter={play}
      onMouseLeave={stop}
      className="group flex flex-col overflow-hidden rounded-2xl bg-white/5 ring-1 ring-white/10 transition duration-200 ease-out hover:ring-white/30"
    >
      <div className={`relative ${ASPECT_CLASS[item.aspectRatio] ?? 'aspect-[9/16]'} bg-black/30`}>
        {!loaded && !hasThumb && (
          <div aria-hidden className="absolute inset-0 animate-pulse bg-white/5" />
        )}
        {inView && (
          <video
            ref={videoRef}
            src={item.videoUrl}
            poster={item.thumbnailUrl || undefined}
            // Кадр не режем: пример должен выглядеть так же, как он выйдет на
            // станции (docs/CREATIVE_SPEC.md).
            className={`h-full w-full object-contain transition-opacity duration-300 ${
              loaded || hasThumb ? 'opacity-100' : 'opacity-0'
            }`}
            loop
            muted
            playsInline
            preload={hasThumb ? 'none' : 'metadata'}
            onLoadedData={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        )}

        <div className="pointer-events-none absolute left-1.5 top-1.5 flex gap-1">
          {item.model && (
            <span className="rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white ring-1 ring-white/15 backdrop-blur-md">
              {item.model}
            </span>
          )}
          {item.durationSec ? (
            <span className="rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white ring-1 ring-white/15 backdrop-blur-md">
              {item.durationSec}с
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
        <p className="truncate text-sm font-semibold text-white" title={item.title}>
          {item.title}
        </p>
        <p className="line-clamp-3 text-xs leading-relaxed text-white/60" title={item.prompt}>
          {item.prompt}
        </p>

        <div className="mt-auto flex gap-1.5 pt-1">
          {onUsePrompt && (
            <button
              type="button"
              onClick={() => onUsePrompt(item.prompt)}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-white/10 px-2 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {t('studio.suggested.usePrompt')}
            </button>
          )}
          <button
            type="button"
            onClick={copy}
            title={t('studio.examples.copyPrompt')}
            aria-label={t('studio.examples.copyPrompt')}
            className="inline-flex items-center justify-center rounded-lg bg-white/10 px-2 py-1.5 text-white transition hover:bg-white/20"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
